import {
  Address,
  Combobox,
  InputTextCustom,
  RadioButtonGroup,
  RadioButtonGroupOption,
} from 'medere-ui-framework'
import { useEffect } from 'react'
import FiscalAddressCombo from '../../../components/FiscalAddressCombo'
import { ComboDataDTO } from '../../../dto/ComboDataDTO'
import { HealthEntitySaveDTO } from '../../../dto/HealthEntitySaveDTO'

import { useHealthEntityService } from '../../../servicesHooks/HealthEntity/useHealthEntityService'

interface HealthEntityFiscalDataTabProps {
  healthEntity: HealthEntitySaveDTO
  setHealthEntity: React.Dispatch<React.SetStateAction<HealthEntitySaveDTO>>
  readOnly?: boolean
}

const yesNoOptions: RadioButtonGroupOption[] = [
  { label: 'Sí', value: 1 },
  { label: 'No', value: 2 },
]

function toCombo(id: number | null | undefined, name: string | null | undefined): ComboDataDTO | null {
  if (id == null || id <= 0) return null
  return { id, name: name ?? '' }
}

function isFiscalAddress(address: Address): boolean {
  const types = address?.types ?? []
  return types.some((type) => {
    const id = Number(type?.id ?? -1)
    const description = String(type?.description ?? '').toLowerCase()
    return id === 3 || description.includes('fiscal')
  })
}

function resolveFiscalAddresses(addresses: Address[]): Array<{ id: number, name: string }> {
  return (addresses ?? [])
    .filter((address) => isFiscalAddress(address))
    .map((address) => ({
      id: Number(address?.address ?? -1),
      name: String(address?.addressdesc ?? '').trim(),
    }))
    .filter((address) => Number.isFinite(address.id) && address.id > 0)
}

interface ReadOnlyFieldProps {
  label: string
  value: string | number | null | undefined
  className?: string
  emptyText?: string
}

function formatReadOnlyValue(value: string | number | null | undefined, emptyText = 'Sin datos'): string {
  if (value == null) return emptyText
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return emptyText
    return String(value)
  }

  const normalized = String(value).trim()
  return normalized !== '' ? normalized : emptyText
}

function ReadOnlyField({ label, value, className = '', emptyText = 'Sin datos' }: Readonly<ReadOnlyFieldProps>) {
  const normalizedValue = formatReadOnlyValue(value, emptyText)
  const isEmptyValue = normalizedValue === emptyText

  return (
    <div className={`health-entity-readonly-field ${className}`.trim()}>
      <span className='health-entity-readonly-field-label'>{label}</span>
      {isEmptyValue ? (
        <em
          className='health-entity-readonly-field-value health-entity-readonly-field-value-muted'
          style={{ fontFamily: 'sans-serif', fontStyle: 'italic' }}
        >
          {normalizedValue}
        </em>
      ) : (
        <span className='health-entity-readonly-field-value'>{normalizedValue}</span>
      )}
    </div>
  )
}

export default function HealthEntityFiscalDataTab({
  healthEntity,
  setHealthEntity,
  readOnly = false,
}: Readonly<HealthEntityFiscalDataTabProps>) {
  const { getHealthEntityPOSByVatCategoryAndIssuer, getHealthEntityInvoiceIssuerEntities, getHealthEntityAfipStatusByVatCustomerCategory, getHealthEntityVatCustomerCategories } = useHealthEntityService()

  useEffect(() => {
    setHealthEntity((prev) => {
      const fiscalAddresses = resolveFiscalAddresses(prev.addresses ?? [])
      const selectedFiscalAddressId = Number(prev.fiscaladdress ?? -1)
      if (selectedFiscalAddressId <= 0) {
        if (fiscalAddresses.length === 1) {
          return {
            ...prev,
            fiscaladdress: fiscalAddresses[0].id,
            fiscaladdressdesc: fiscalAddresses[0].name,
          }
        }
        return prev
      }

      const selectedAddress = (prev.addresses ?? [])
        .find((address) => Number(address?.address ?? -1) === selectedFiscalAddressId)

      if (!selectedAddress || !isFiscalAddress(selectedAddress)) {
        if (fiscalAddresses.length === 1) {
          return {
            ...prev,
            fiscaladdress: fiscalAddresses[0].id,
            fiscaladdressdesc: fiscalAddresses[0].name,
          }
        }
        return {
          ...prev,
          fiscaladdress: null,
          fiscaladdressdesc: '',
        }
      }

      return prev
    })
  }, [healthEntity.addresses, setHealthEntity])

  return (
    <div className='health-entity-fiscal-tab'>
      <section className='health-entity-section'>
        <h4 className='health-entity-section-title'>Identidad fiscal</h4>
        {readOnly ? (
          <div className='health-entity-fiscal-grid health-entity-fiscal-grid-identity'>
            <ReadOnlyField className='health-entity-fiscal-field-wide' label='Razón social' value={healthEntity.businessname} />
            <ReadOnlyField label='CUIT' value={healthEntity.cuit} />
          </div>
        ) : (
          <div className='health-entity-fiscal-grid health-entity-fiscal-grid-identity'>
            <InputTextCustom
              className='w-full health-entity-fiscal-field-wide'
              label='Razón social'
              value={healthEntity.businessname}
              onChange={(e) => setHealthEntity((prev) => ({ ...prev, businessname: e?.target?.value ?? '' }))}
              floatLabel
              disable={readOnly}
            />

            <InputTextCustom
              className='w-full'
              label='CUIT'
              value={healthEntity.cuit}
              onChange={(e) => setHealthEntity((prev) => ({ ...prev, cuit: e?.target?.value ?? '' }))}
              placeholder='Ej: 30-12345678-2'
              floatLabel
              disable={readOnly}
            />
          </div>
        )}
      </section>

      <section className='health-entity-section'>
        <h4 className='health-entity-section-title'>Situación impositiva</h4>
        {readOnly ? (
          <div className='health-entity-fiscal-grid health-entity-fiscal-grid-tax'>
            <ReadOnlyField label='Condición IVA' value={healthEntity.vatcategorydesc} />
            <ReadOnlyField label='Estado ante ARCA' value={healthEntity.afipstatusdesc} />
          </div>
        ) : (
          <div className='health-entity-fiscal-grid health-entity-fiscal-grid-tax'>
            <Combobox
              className='w-full'
              floatLabel
              label='Condición IVA'
              optionLabel='name'
              placeholder='Seleccionar'
              value={toCombo(healthEntity.vatcategory, healthEntity.vatcategorydesc)}
              getItems={getHealthEntityVatCustomerCategories}
              disable={readOnly}
              setValue={(value: ComboDataDTO | null) => {
                setHealthEntity((prev) => ({
                  ...prev,
                  vatcategory: value?.id ?? null,
                  vatcategorydesc: value?.name ?? '',
                  afipstatus: null,
                  afipstatusdesc: '',
                  defaultpos: null,
                  defaultposname: '',
                }))
              }}
            />

            <Combobox
              className='w-full'
              floatLabel
              label='Estado ante ARCA'
              optionLabel='name'
              placeholder='Seleccionar'
              disable={readOnly || !healthEntity.vatcategory || healthEntity.vatcategory <= 0}
              reLoadItemsValue={healthEntity.vatcategory}
              value={toCombo(healthEntity.afipstatus, healthEntity.afipstatusdesc)}
              getItems={getHealthEntityAfipStatusByVatCustomerCategory}
              params={{ vatCustomerCategory: healthEntity.vatcategory ?? -1 }}
              setValue={(value: ComboDataDTO | null) => {
                setHealthEntity((prev) => ({
                  ...prev,
                  afipstatus: value?.id ?? null,
                  afipstatusdesc: value?.name ?? '',
                }))
              }}
            />
          </div>
        )}
      </section>

      <section className='health-entity-section'>
        <h4 className='health-entity-section-title'>Domicilio</h4>
        <div className='health-entity-fiscal-address-row'>
          {readOnly ? (
            <ReadOnlyField
              label='Domicilio fiscal'
              value={healthEntity.fiscaladdressdesc}
              emptyText='Dirección fiscal faltante'
            />
          ) : (
            <FiscalAddressCombo
              medereentity={healthEntity.medereentity ?? -1}
              localAddresses={healthEntity.addresses ?? []}
              fiscalAddress={toCombo(healthEntity.fiscaladdress, healthEntity.fiscaladdressdesc)}
              disabled={readOnly}
              onChange={(value) => {
                setHealthEntity((prev) => ({
                  ...prev,
                  fiscaladdress: value?.id ?? null,
                  fiscaladdressdesc: value?.name ?? '',
                }))
              }}
            />
          )}
        </div>
      </section>

      <section className='health-entity-section'>
        <h4 className='health-entity-section-title'>Configuración de facturación</h4>
        {readOnly ? (
          <div className='health-entity-billing-grid health-entity-billing-grid-config'>
            <ReadOnlyField label='Requiere FCE' value={healthEntity.electroniccreditinvoice ? 'Sí' : 'No'} />
            <ReadOnlyField label='Emisor de facturas' value={healthEntity.definvoiceissuername} />
            <ReadOnlyField label='Punto de venta' value={healthEntity.defaultposname} />
          </div>
        ) : (
          <div className='health-entity-billing-grid health-entity-billing-grid-config'>
            <div className='health-entity-billing-fce'>
              <RadioButtonGroup
                label='Requiere FCE'
                options={yesNoOptions}
                value={healthEntity.electroniccreditinvoice ? 1 : 2}
                setValue={(value) => {
                  setHealthEntity((prev) => ({
                    ...prev,
                    electroniccreditinvoice: value === 1,
                  }))
                }}
                generalClassName='health-entity-fce-group'
                classNameItem='center health-entity-fce-option'
                orientation='row'
                itemWidth={25}
                disable={readOnly}
              />
            </div>

            <Combobox
              className='w-full'
              optionLabel='name'
              placeholder='Seleccionar'
              value={toCombo(healthEntity.defaultinvoiceissuerentity, healthEntity.definvoiceissuername)}
              getItems={getHealthEntityInvoiceIssuerEntities}
              floatLabel
              label='Emisor de facturas'
              disable={readOnly}
              setValue={(value: ComboDataDTO | null) => {
                setHealthEntity((prev) => ({
                  ...prev,
                  defaultinvoiceissuerentity: value?.id ?? null,
                  definvoiceissuername: value?.name ?? '',
                  defaultpos: null,
                  defaultposname: '',
                }))
              }}
            />

            <Combobox
              className='w-full'
              optionLabel='name'
              placeholder='Seleccionar'
              value={toCombo(healthEntity.defaultpos, healthEntity.defaultposname)}
              params={{
                catreceiver: healthEntity.vatcategory ?? -1,
                invoicingcompany: healthEntity.defaultinvoiceissuerentity ?? -1,
              }}
              reLoadItemsValue={`${healthEntity.vatcategory ?? -1}-${healthEntity.defaultinvoiceissuerentity ?? -1}`}
              getItems={getHealthEntityPOSByVatCategoryAndIssuer}
              floatLabel
              label='Punto de venta'
              disable={readOnly || !healthEntity.vatcategory || !healthEntity.defaultinvoiceissuerentity}
              setValue={(value: ComboDataDTO | null) => {
                setHealthEntity((prev) => ({
                  ...prev,
                  defaultpos: value?.id ?? null,
                  defaultposname: value?.name ?? '',
                }))
              }}
            />
          </div>
        )}
      </section>
    </div>
  )
}
